<template>
  <v-container
    class="products-list-page"
    fluid>
    <img-popup-preview
      v-model="previewEnabled"
      :img-src="currentImgPreview" />
    <v-row
      class="head-search">
      <v-col
        cols="6"
        sm="9"
        md="6"
        lg="auto"
        xl="auto">
        <v-btn
          outlined
          color="success"
          :loading="loading"
          @click="exportProductsToCSV()">
          <v-icon color="green">
            mdi-microsoft-excel
          </v-icon>
          Export {{ totalProduct }}
        </v-btn>
      </v-col>
      <v-spacer></v-spacer>
      <v-col
        cols="12"
        lg="8"
        xl="6">
        <search-by-tags-and-brand
          v-model="query"
          append-icon="mdi-magnify"
          tags-width="300px"
          show-collection
          :is-mobile="isMobile"
          @on-search="searchProduct()" />
      </v-col>
    </v-row>

    <v-row
      v-for="(product, index) in products"
      :key="`product-${index}`"
      class="white elevation-3 my-8"
      :class="isMobile ? `mx-2` : ''">
      <v-col :cols="12">
        <product
          :product-id="product.id"
          :product-name="product.name"
          :is-sold-out="checkIsOutOfStock(product)"
          :model="product.model"
          :brand="product.brand"
          :skus="product.skus"
          :gw-collection="product.gwCollection"
          :categories="product.categories"
          :product-prototype="product.productPrototype || null"
          :group-categories="product.groupCategories"
          :tags="product.tags"
          :photo-urls="product.photoUrls"
          :alias-id="product.aliasId"
          :warehouses="mapWarehouse"
          :bc-sync="product.bcSync"
          :ac2-sync="product.ac2Sync"
          :ac2-syncing="isSyncingAc2(product.id)"
          :is-mobile="isMobile"
          @on-preview-image="previewImg($event)"
          @on-sync-ac2="syncAc2($event)" />
      </v-col>
    </v-row>

    <v-row
      v-if="isNotHaveProducts"
      justify="center"
      align="center">
      <v-col
        cols="auto"
        class="my-15">
        <p>There aren't any products. :( </p>
      </v-col>
    </v-row>

    <v-skeleton-loader
      v-for="loader in loaders"
      :key="`loader-${loader}`"
      :loading="true"
      height="150"
      class="loader-product-list"
      type="list-item-avatar-three-line" />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ProductProvider from '@/resources/ProductProvider'
import SearchByTagsAndBrand from '@/components/SearchByTagsAndBrand.vue'
import ImgPopupPreview from '@/components/ImgModalPreview.vue'
import ConvertQueryStringToArray from '@/assets/js/ConvertQueryStringToArray'
import Product from '../components/ProductBox.vue'

const ProductService = new ProductProvider()

export default {
  name: 'ProductList',
  components: {
    product: Product,
    ImgPopupPreview,
    SearchByTagsAndBrand
  },
  data () {
    return {
      previewEnabled: false,
      currentImgPreview: null,
      products: [],
      query: {
        page: 0,
        limit: 15,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        search: '',
        tags: [],
        tagOperation: 'OR',
        brand: 'all',
        collection: ''
      },
      totalPage: 1,
      totalProduct: 0,
      loading: false,
      getting: false,
      isLoadComplete: false,
      syncingAc2Ids: []
    }
  },
  computed: {
    ...mapGetters({
      mapWarehouse: 'Store/mapWarehouse',
      warehouses: 'Store/warehouse'
    }),
    isNotHaveProducts () {
      return this.products.length === 0 && this.isLoadComplete
    },
    loaders () {
      return this.isLoadComplete ? 0 : 2
    },
    isMobile () {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm
    }
  },
  created () {
    window.addEventListener('scroll', this.handleScroll)
    this.queryTags()
    this.fetchProducts()
    this.$store.dispatch('Store/getWarehouse')
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    ...mapActions({
      setErrorPage: 'Components/setErrorPage'
    }),
    checkIsOutOfStock (product) {
      let result = true
      const warehouses = this.warehouses.filter((w) => w.status === 'active' && +w.id !== 0)
      for (const sku of product.skus) {
        for (const st of sku.stock) {
          const warehouseStock = warehouses.find((wh) => +wh.id === +st.warehouse.id)
          if (warehouseStock) {
            const offlineMinStock = warehouseStock?.offlineMinStock || 0
            const webStock = st.onHandQty - st.onReservedQty - offlineMinStock
            if (webStock > 0) {
              result = false
              break
            }
          }
        }
      }
      return result
    },
    previewImg (img) {
      this.currentImgPreview = img
      this.previewEnabled = !!this.currentImgPreview
    },
    queryTags () {
      const result = ConvertQueryStringToArray(this.$route.query)

      this.query.search = result.search ? result.search : ''
      this.query.tags = result.tags ? result.tags : []
      this.query.tagOperation = result.tagOperation ? result.tagOperation : 'OR'
      this.query.brand = result.brand ? result.brand : 'all'
      this.query.collection = result.collection ? result.collection : ''
    },
    searchProduct () {
      this.isLoadComplete = false
      this.query.page = 0
      this.getting = false

      this.products = []
      this.$router.push({
        name: 'Products',
        query: {
          search: this.query.search,
          tags: this.query.tags || [],
          tagOperation: this.query.tagOperation,
          brand: this.query.brand,
          collection: this.query.collection
        }
      }).catch((error) => {
        console.error(error)
      })
      this.fetchProducts()
    },
    async fetchProducts () {
      this.getting = true
      if (this.query.page < this.totalPage) {
        this.query.page++
        try {
          this.queryTags()

          const { data } = await ProductService.getProducts({
            page: this.query.page,
            limit: this.query.limit,
            search: this.query.search,
            tags: this.query.tags,
            tagOperation: this.query.tagOperation,
            brand: this.query.brand,
            collection: this.query.collection
          })
          this.totalPage = data.pages
          this.totalProduct = data.total
          this.products.push(...data.results)
        } catch (error) {
          console.error('fetchProducts', error)
          this.setErrorPage(error.code)
        }
      }
      this.isLoadComplete = this.query.page >= this.totalPage
      this.getting = false
    },
    handleScroll (event) {
      const e = event.target.scrollingElement
      const scrollY = e.scrollHeight - e.clientHeight - 1000
      if (e.scrollTop >= scrollY && !this.getting) {
        this.fetchProducts()
      }
    },
    async exportProductsToCSV () {
      try {
        this.loading = true
        const limit = 3000
        const totalRound = Math.ceil(this.totalProduct / limit)

        for (let page = 1; page <= totalRound; page++) {
          // eslint-disable-next-line no-await-in-loop
          const data = await ProductService.exportProduct({
            limit,
            page,
            search: this.query.search,
            tags: this.query.tags,
            tagOperation: this.query.tagOperation,
            brand: this.query.brand,
            collection: this.query.collection
          })

          const bom = '\uFEFF'
          const blob = new Blob([bom + data], { type: 'text/csv;charset=utf-8;' })
          const url = window.URL.createObjectURL(blob)

          const link = document.createElement('a')
          link.href = url
          const random = new Date().getTime()
          link.setAttribute('download', `Product-${random}-${page.toString().padStart(3, '0')}.csv`)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      } catch (error) {
        console.error('fetchProductsToExport', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error ${error.code} : ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    isSyncingAc2 (productId) {
      return this.syncingAc2Ids.includes(productId)
    },
    async syncAc2 (productId) {
      if (this.isSyncingAc2(productId)) {
        return
      }

      this.syncingAc2Ids.push(productId)
      try {
        await ProductService.syncAc2(productId)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'Product sync request added to queue',
          type: 'success'
        })
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error code ${error.code}: ${error.message}`,
          type: 'error'
        })
        console.error('syncAc2', error)
      } finally {
        this.syncingAc2Ids = this.syncingAc2Ids.filter((id) => id !== productId)
      }
    }
  }
}
</script>
